import React, { useState, useEffect, useRef } from 'react';
import styles from './InternetSpeedPage.module.scss';
import InternetSpeedService from 'service/InternetSpeedService';
import { SpeedTestIcon, GlobeIcon, ServerIcon } from 'components/CustomIcons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload, faSignal, faNetworkWired } from '@fortawesome/free-solid-svg-icons';

const InternetSpeedPage = ({ goBack }) => {
    const [status, setStatus] = useState('idle'); // idle, ping, download, upload, finished
    const [displaySpeed, setDisplaySpeed] = useState(0); 
    const [gaugeProgress, setGaugeProgress] = useState(0); 
    
    // Stats now store objects
    const [ping, setPing] = useState('--');
    const [jitter, setJitter] = useState('--');
    const [downloadStats, setDownloadStats] = useState({ max: '--', avg: '--' });
    const [uploadStats, setUploadStats] = useState({ max: '--', avg: '--' });
    const [peakSpeed, setPeakSpeed] = useState(0); 
    
    const [clientInfo, setClientInfo] = useState({
        ip: 'Checking...',
        provider: 'Detecting...',
        location: 'Auto',
        isWhiteIp: false
    });

    const abortControllerRef = useRef(null);
    const speedValueRef = useRef(0);
    const renderSpeedRef = useRef(0); 
    const animationFrameRef = useRef(null);

    useEffect(() => {
        InternetSpeedService.getClientInfo()
            .then(async (response) => {
                if(response.data && response.data.ip) {
                    const ip = response.data.ip;
                    try {
                        const geoResponse = await fetch(`http://ip-api.com/json/${ip}`);
                        const geoData = await geoResponse.json();
                        if(geoData.status === 'success') {
                             setClientInfo({
                                ip: ip,
                                provider: geoData.isp || 'Unknown',
                                location: `${geoData.city}, ${geoData.countryCode}`,
                                isWhiteIp: true
                            });
                        } else {
                             setClientInfo(prev => ({ ...prev, ip: ip, provider: 'Local/Private', location: 'Local Network' }));
                        }
                    } catch (e) {
                        setClientInfo(prev => ({ ...prev, ip: ip }));
                    }
                }
            })
            .catch(error => { console.warn('Failed to fetch client IP', error); });
    }, []);

    // --- Animation Loop ---
    const lastRenderTime = useRef(0);

    const animate = (time) => {
        if (!renderingRef.current) return;

        // Throttle updates to ~30 FPS to save mobile CPU
        if (time - lastRenderTime.current < 32) {
             animationFrameRef.current = requestAnimationFrame(animate);
             return;
        }
        lastRenderTime.current = time;

        const diff = speedValueRef.current - renderSpeedRef.current;
        // Faster attack (0.2), Slower decay (0.05) for smoother "VU meter" feel
        const ease = diff > 0 ? 0.2 : 0.05;
        
        if (Math.abs(diff) > 0.01) {
             renderSpeedRef.current += diff * ease;
        } else {
             renderSpeedRef.current = speedValueRef.current;
        }

        // Only update state if value changed significantly
        const newSpeed = Math.max(0, renderSpeedRef.current);
        const maxScale = 1000;
        let progress = newSpeed / maxScale;
        const newProgress = Math.min(progress, 1.05);

        setDisplaySpeed(prev => {
            if (Math.abs(prev - newSpeed) < 0.1) return prev;
            return newSpeed;
        });

        setGaugeProgress(prev => {
            if (Math.abs(prev - newProgress) < 0.001) return prev;
            return newProgress;
        });

        animationFrameRef.current = requestAnimationFrame(animate);
    };

    const renderingRef = useRef(false);

    useEffect(() => {
        renderingRef.current = true;
        animationFrameRef.current = requestAnimationFrame(animate);
        return () => {
            renderingRef.current = false;
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, []);

    const startTest = async () => {
        if (status !== 'idle' && status !== 'finished') return;
        
        setStatus('ping');
        setPing('--'); 
        setDownloadStats({ max: '--', avg: '--' }); // Reset as object
        setUploadStats({ max: '--', avg: '--' });
        setJitter('--');
        setPeakSpeed(0);

        speedValueRef.current = 0;
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        try {
            // 1. Ping
            setPing('...');
            try { await InternetSpeedService.checkPing(); } catch(e) {}

            const pings = [];
            for(let i=0; i<10; i++) {
                if(signal.aborted) return;
                const start = performance.now();
                await InternetSpeedService.checkPing();
                const end = performance.now();
                const duration = end - start;
                pings.push(duration);
                speedValueRef.current = duration; 
                await new Promise(r => setTimeout(r, 60));
            }

            if(pings.length) {
                const minPing = Math.min(...pings);
                const avgPing = pings.reduce((a,b)=>a+b,0)/pings.length;
                const jitterCalc = pings.reduce((a,b) => a + Math.abs(b - avgPing), 0) / pings.length;
                setPing(minPing.toFixed(0));
                setJitter(jitterCalc.toFixed(0));
            }
            speedValueRef.current = 0;

            // 2. Download
            setStatus('download');
            setPeakSpeed(0);
            await runDownloadTest(signal);

            // 3. Upload
            setStatus('upload');
            setPeakSpeed(0);
            await new Promise(r => setTimeout(r, 500));
            await runUploadTest(signal);

            setStatus('finished');
            speedValueRef.current = 0;
            setPeakSpeed(0);

        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                console.log('Test aborted');
            } else {
                console.error('Speedtest error:', error);
                alert('Connection Error. Check backend/network.');
                setStatus('idle');
            }
        }
    };

    const runDownloadTest = async (signal) => {
        const THREADS = 4;
        const DURATION = 8000;
        const threadStates = new Array(THREADS).fill(0);
        
        // Smoothing buffers to prevent un-renderable spikes
        const speedBuffer = [];
        const BUFFER_SIZE = 3; 

        let maxSpeed = 0;
        let lastLoaded = 0;
        let lastTime = performance.now();
        
        const phaseController = new AbortController();
        const onMainAbort = () => phaseController.abort();
        signal.addEventListener('abort', onMainAbort);
        const timer = setTimeout(() => phaseController.abort(), DURATION);
        
        const startTime = performance.now();

        const downloadWorker = async (index) => {
             // Loop to keep downloading for the duration without OOM
             while (!phaseController.signal.aborted) {
                 try {
                     let requestLoaded = 0;
                     const previousTotal = threadStates[index]; 
                     // Use smaller chunk (e.g., 20MB) to save RAM on mobile
                     // 500MB used to crash mobile browsers
                     const size = 20 * 1024 * 1024; 
                     
                     await InternetSpeedService.runDownload(size, phaseController.signal, (e) => {
                         requestLoaded = e.loaded;
                         threadStates[index] = previousTotal + requestLoaded;
                     });
                 } catch (e) { 
                     break; 
                 }
             }
        };

        const monitorInterval = setInterval(() => {
            const now = performance.now();
            const totalLoaded = threadStates.reduce((a,b)=>a+b, 0);
            
            // Instant Speed (Windowed)
            const timeDiff = (now - lastTime) / 1000;
            const bytesDiff = totalLoaded - lastLoaded;
            
            if (timeDiff > 0.1) {
                const rawInstantSpeed = (bytesDiff * 8) / (timeDiff * 1000000); // Mbps
                
                // Add to buffer
                speedBuffer.push(rawInstantSpeed);
                if (speedBuffer.length > BUFFER_SIZE) speedBuffer.shift();

                // Calculate Smoothed Speed (This is what the Stats AND Needle use)
                const smoothedInstant = speedBuffer.reduce((a,b)=>a+b,0) / speedBuffer.length;

                // Cumulative Average
                const totalElapsed = (now - startTime) / 1000;
                const avgSpeed = (totalLoaded * 8) / (totalElapsed * 1000000);

                // Update Max based on SMOOTHED speed so Needle can actually catch it
                if (smoothedInstant > maxSpeed) maxSpeed = smoothedInstant;
                
                // Needle follows Instant (Smoothed)
                speedValueRef.current = smoothedInstant;
                
                // Stats
                setDownloadStats({ max: maxSpeed.toFixed(0), avg: avgSpeed.toFixed(0) });
                setPeakSpeed(smoothedInstant); 
                
                lastLoaded = totalLoaded;
                lastTime = now;
            }
        }, 150);

        await Promise.all(new Array(THREADS).fill(0).map((_, i) => downloadWorker(i)));
        
        clearInterval(monitorInterval);
        clearTimeout(timer);
        signal.removeEventListener('abort', onMainAbort);
        
        const finalTime = (performance.now() - startTime) / 1000;
        const total = threadStates.reduce((a,b)=>a+b, 0);
        const avgSpeed = (total * 8) / (finalTime * 1000000);
        
        setDownloadStats({ max: maxSpeed.toFixed(0), avg: avgSpeed.toFixed(0) });
        speedValueRef.current = 0;
    };

    const runUploadTest = async (signal) => {
        const THREADS = 4;
        const DURATION = 8000; 
        const threadStates = new Array(THREADS).fill(0);
        
        // Smoothing buffers
        const speedBuffer = [];
        const BUFFER_SIZE = 3; 

        let maxSpeed = 0;
        let lastLoaded = 0;
        let lastTime = performance.now();
        
        const phaseController = new AbortController();
        const onMainAbort = () => phaseController.abort();
        signal.addEventListener('abort', onMainAbort);
        const timer = setTimeout(() => phaseController.abort(), DURATION);

        const startTime = performance.now();
        
        // OPTIMIZATION: Create a smaller, efficient blob (5MB)
        // 50MB was causing freezes on mobile devices due to memory allocation
        const size = 5 * 1024 * 1024;
        const chunk = new Uint8Array(size);
        // Fill sparsely to avoid compression hacks by ISPs while keeping it fast
        for(let i = 0; i < size; i += 4096) chunk[i] = (Math.random() * 255) | 0;
        const blob = new Blob([chunk]);

        const uploadWorker = async (index) => {
             while (!phaseController.signal.aborted) {
                 try {
                     let requestLoaded = 0;
                     const previousTotal = threadStates[index]; 
                     await InternetSpeedService.runUpload(blob, phaseController.signal, (e) => {
                         requestLoaded = e.loaded;
                         threadStates[index] = previousTotal + requestLoaded;
                     });
                 } catch(e) { break; }
             }
        };

        const monitorInterval = setInterval(() => {
            const now = performance.now();
            const totalLoaded = threadStates.reduce((a,b)=>a+b, 0);

            // Instant Speed
            const timeDiff = (now - lastTime) / 1000;
            const bytesDiff = totalLoaded - lastLoaded;
            
            if (timeDiff > 0.1) {
                const rawInstantSpeed = (bytesDiff * 8) / (timeDiff * 1000000);

                // Add to buffer
                speedBuffer.push(rawInstantSpeed);
                if (speedBuffer.length > BUFFER_SIZE) speedBuffer.shift();

                // Smoothed
                const smoothedInstant = speedBuffer.reduce((a,b)=>a+b,0) / speedBuffer.length;
                
                const totalElapsed = (now - startTime) / 1000;
                const avgSpeed = (totalLoaded * 8) / (totalElapsed * 1000000);

                if (smoothedInstant > maxSpeed) maxSpeed = smoothedInstant;
                
                speedValueRef.current = smoothedInstant;
                setUploadStats({ max: maxSpeed.toFixed(0), avg: avgSpeed.toFixed(0) });
                setPeakSpeed(smoothedInstant);
                
                lastLoaded = totalLoaded;
                lastTime = now;
            }
        }, 150);

        await Promise.all(new Array(THREADS).fill(0).map((_, i) => uploadWorker(i)));
        
        clearInterval(monitorInterval);
        clearTimeout(timer);
        signal.removeEventListener('abort', onMainAbort);
        
        const finalTime = (performance.now() - startTime) / 1000;
        const total = threadStates.reduce((a,b)=>a+b, 0);
        const avgSpeed = (total * 8) / (finalTime * 1000000);
        
        setUploadStats({ max: maxSpeed.toFixed(0), avg: avgSpeed.toFixed(0) });
        speedValueRef.current = 0;
    };

    // --- SVG Helpers ---
    // Resized for "Bigger" look (ViewBox 300x300)
    // Center 150,150. Max radius ~145.
    const normalizedRadius = 135; 
    const circumference = normalizedRadius * 2 * Math.PI;
    const arcCoverage = 0.75; // 270 degrees
    const arcLength = circumference * arcCoverage; 
    
    // Correct Dash Offset: 
    // Start empty (offset = arcLength) -> End full (offset = 0)
    // Formula: arcLength * (1 - progress)
    const strokeDashoffset = arcLength * (1 - gaugeProgress);

    // Generate Ticks (0, 100, ..., 1000)
    const ticks = [];
    const tickCount = 11; 
    for(let i=0; i<tickCount; i++) {
        const val = i * 100;
        const label = val === 1000 ? '1G+' : val;
        
        // Start 135deg (Bottom Left), End 405deg (Bottom Right)
        const startAngle = 135;
        const sweep = 270;
        const angleDeg = startAngle + (i / (tickCount - 1)) * sweep;
        const angleRad = (angleDeg * Math.PI) / 180;
        
        // Position Numbers INSIDE the track
        const textRadius = normalizedRadius - 24; 
        const x = 150 + textRadius * Math.cos(angleRad);
        const y = 150 + textRadius * Math.sin(angleRad);
        
        ticks.push(
            <text 
                key={val} 
                x={x} 
                y={y} 
                textAnchor="middle" 
                alignmentBaseline="middle" 
                fill="rgba(255,255,255,0.7)" 
                fontSize="12" 
                fontWeight="600"
                fontFamily="Rajdhani"
            >
                {label}
            </text>
        );
    }

    // Helper for Status Text
    const getStatusText = (s) => {
        switch(s) {
            case 'idle': return 'ГОТОВ';
            case 'ping': return 'ПИНГ...';
            case 'download': return 'СКАЧИВАНИЕ';
            case 'upload': return 'ЗАГРУЗКА';
            case 'finished': return 'РЕЗУЛЬТАТ';
            default: return s.toUpperCase();
        }
    };

    return (
        <div className={styles.container}>
            {/* Back Button Removed */}
            <div className={styles.speedtestCard}>
                
                <div className={styles.header}>
                    <div className={styles.logo}>
                         <SpeedTestIcon size={28} />
                         NCORE<span style={{fontWeight: 300, color: '#aaa'}}>SPEED</span>
                    </div>
                </div>

                <div className={styles.gaugeWrapper}>
                    <svg className={styles.gaugeSvg} viewBox="0 0 300 300">
                        <defs>
                            {/* Adjusted Gradient to ensure full coverage Left-to-Right */}
                            <linearGradient id="gradientSpeed" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="userSpaceOnUse">
                                <stop offset="0%" stopColor="#00d2ff" />
                                <stop offset="50%" stopColor="#3a7bd5" />
                                <stop offset="100%" stopColor="#9d50bb" />
                            </linearGradient>
                        </defs>
                        
                         {/* Track & Progress in Rotated Group */}
                        <g transform="translate(150, 150) rotate(135)">
                             <circle r={normalizedRadius} className={styles.track} strokeDasharray={`${arcLength} ${circumference}`} strokeLinecap="round" />
                             
                             <circle r={normalizedRadius} className={styles.progress} 
                                strokeDasharray={`${arcLength} ${circumference}`} 
                                strokeDashoffset={strokeDashoffset} 
                                strokeLinecap="round"
                             />
                        </g>

                         {/* Ticks in Global Space */}
                         {ticks}
                    </svg>
                    
                    <div className={styles.centerDisplay}>
                         <div className={styles.statusLabel}>
                             {getStatusText(status)}
                         </div>
                         <div className={styles.speedValue}>
                             {displaySpeed.toFixed(0)}
                         </div>
                         <div className={styles.speedUnit}>Mbps</div>
                         {(status === 'download' || status === 'upload') && (
                             <div style={{color: '#00d2ff', fontSize: '14px', marginTop: '5px'}}>
                                 Пик: {peakSpeed.toFixed(0)}
                             </div>
                         )}
                    </div>

                    <div className={styles.actionButton}>
                        {(status === 'idle' || status === 'finished') && (
                            <button onClick={startTest}>
                                {status === 'finished' ? 'ПОВТОР' : 'НАЧАТЬ'}
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles.statsGrid}>
                    <div className={`${styles.statCard} ${status === 'ping' ? styles.active : ''}`}>
                        <FontAwesomeIcon icon={faSignal} className={styles.statIcon} />
                        <div className={styles.statLabel}>Пинг</div>
                        <div className={styles.statValue}>
                            {ping} <span style={{fontSize: '0.5em'}}>мс</span>
                        </div>
                        <div style={{fontSize: '10px', color: '#666'}}>Джиттер: {jitter}</div>
                    </div>
                    
                    <div className={`${styles.statCard} ${status === 'download' ? styles.active : ''}`}>
                        <FontAwesomeIcon icon={faDownload} className={styles.statIcon} />
                        <div className={styles.statLabel}>Скачивание</div>
                        
                        <div className={styles.statValue}>
                            {downloadStats.avg} <span style={{fontSize: '0.5em'}}>Сред</span>
                        </div>
                         <div className={styles.statSubValue} style={{fontSize: '12px', color: '#888'}}>
                            Макс: {downloadStats.max} Mbps
                        </div>
                    </div>
                    
                    <div className={`${styles.statCard} ${status === 'upload' ? styles.active : ''}`}>
                         <FontAwesomeIcon icon={faUpload} className={styles.statIcon} />
                        <div className={styles.statLabel}>Загрузка</div>
                        <div className={styles.statValue}>
                            {uploadStats.avg} <span style={{fontSize: '0.5em'}}>Сред</span>
                        </div>
                         <div className={styles.statSubValue} style={{fontSize: '12px', color: '#888'}}>
                            Макс: {uploadStats.max} Mbps
                        </div>
                    </div>
                </div>
                
                <div className={styles.footerInfo}>
                    <div className={styles.infoItem}>
                         <span className={styles.label}>Провайдер</span>
                         <div className={styles.value}>
                             <FontAwesomeIcon icon={faNetworkWired} />
                             {clientInfo.provider}
                         </div>
                    </div>
                    <div className={styles.infoItem}>
                         <span className={styles.label}>IP Адрес</span>
                         <div className={styles.value}>
                             <GlobeIcon size={16} />
                             {clientInfo.ip}
                         </div>
                    </div>
                     <div className={styles.infoItem}>
                         <span className={styles.label}>Сервер</span>
                         <div className={styles.value}>
                             <ServerIcon size={16} />
                             {clientInfo.location}
                         </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InternetSpeedPage;
